<script setup>
// A conversa de teste (L-25): a da página do link e a do Playground do agente, que eram duas e
// divergiam. As bolhas de pergunta e resposta, o "digitando" e o campo de texto, que fica sempre à
// vista no pé da tela — no celular, a lista rola e o campo não sai do lugar. Sem conversa, o lugar
// dela é do que testar e das perguntas sugeridas (o slot `#vazio` troca isso).
//
// O que muda entre as telas vem de fora: os turnos, do transporte de cada uma
// (`useConversaDoMotor`, ou `useConversaDoCanal` no modo canal); `publico` diz para quem se
// escreve — o visitante da página ou a equipe no Playground, que vê o detalhe do erro, por que o
// agente não leu um arquivo e que a ferramenta que falhou pede conferir a configuração —; `altura`
// diz se a conversa ocupa a página ou é uma seção de altura fixa; e o slot `#depois-da-resposta`
// põe algo embaixo de cada resposta (o rastro, no Playground).
//
// Esta conversa entra na página do link, que tem CSP sem `unsafe-eval` e traduções próprias: nada
// daqui importa store, router, `dashboard/i18n`, `dashboard/api`, `useAlert` nem floating-vue, e
// os textos são os de `CONVERSA_DE_TESTE` (`i18n/`). Um spec confere as duas coisas.
//
// Cada bolha mostra a hora dela (L-18), e cada resposta tem o botão de copiar
// (`RespostaDoAgente`). O texto do agente passa pelo MessageFormatter (markdown-it com
// `html: false`): HTML que venha do modelo sai como texto, e o resultado ainda passa pelo DOMPurify.
//
// No modo canal (L-13) o turno traz `respostas`, uma por mensagem que chegou na conversa da caixa:
// a do agente e a da pessoa da equipe que assumiu depois da transferência, que aparece com o selo
// da equipe.
//
// A foto e o PDF (L-19) entram pelo clipe, ao lado do microfone: a página recusa o que não é foto nem
// PDF, o que passa de 5 MB e o quarto arquivo, com o motivo, e a bolha mostra a miniatura da foto ou
// o nome do PDF. Quando a versão publicada espera texto junto do arquivo, no modo motor, o envio só
// libera com o texto (`anexoPedeTexto`). O arquivo que o agente não vai ler é dito antes do envio
// (L-31): nenhum, com a leitura de anexos desligada (`leAnexos`), ou o PDF, com o par de modelos que
// não lê documento (`leDocumentos`); o slot `#nao-analisa` troca a primeira frase (o Playground põe
// ali o caminho para ligar). Embaixo da resposta vai o arquivo que o agente não leu: o que ele não
// conseguiu ler, ou o que chegou a um agente que não analisa arquivo.
//
// O arquivo escolhido (L-28) aparece numa miniatura com o X de tirar no canto (`ChipDeAnexo`), e o
// clique abre o visor no meio da tela (`VisorDeAnexo`). A miniatura da bolha
// também abre o visor. Antes de enviar, o visor anota a foto escolhida (L-29), e a anotada toma o
// lugar dela; a da bolha já foi, e só se vê. O visor é um pedaço à parte do pacote (~10 KB), que só
// baixa no primeiro clique; o editor, o pdf.js e o pdf-lib são pedaços dele, que só baixam no primeiro
// "Anotar", no primeiro PDF aberto e no primeiro PDF anotado salvo.
//
// A nota de voz (L-15) sai do gravador compartilhado com o Playground (`useGravadorDeAudio`). O
// microfone só aparece quando a versão publicada transcreve e o navegador grava; a bolha diz
// "Transcrevendo…", depois o que o agente ouviu, ou por que não ouviu. Texto puro, sem markdown: é
// o que a máquina transcreveu. Enquanto grava, as ondas (A-13) mostram que o microfone capta.
// Depois de enviada, a nota de voz toca na bolha (A-15, `turno.som`): a gravação desta aba, ou, no
// modo canal, o anexo da mensagem. A foto e o PDF escolhidos vão com ela (A-16): `audio` sai com a
// gravação e os arquivos, e a bolha da voz mostra os dois.
import {
  computed,
  nextTick,
  onBeforeUnmount,
  ref,
  shallowRef,
  watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useGravadorDeAudio } from 'shared/composables/useGravadorDeAudio';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import ChipDeAnexo from 'shared/components/anexos/ChipDeAnexo.vue';
import OndasDoMicrofone from 'shared/components/audio/OndasDoMicrofone.vue';
import AudioEnviado from 'shared/components/audio/AudioEnviado.vue';
import RespostaDoAgente from './RespostaDoAgente.vue';
import AnexosDaBolha from './AnexosDaBolha.vue';
import { horaDe } from './hora';
import { useMotivoDoArquivo } from './motivoDoArquivo';
import {
  POR_MENSAGEM,
  TIPOS_ACEITOS,
  escolherArquivos,
  liberarPrevias,
  paraATela,
} from './anexos';

const props = defineProps({
  oQueTestar: { type: String, default: '' },
  perguntas: { type: Array, default: () => [] },
  turnos: { type: Array, default: () => [] },
  ocupada: { type: Boolean, default: false },
  // O limite por visitante, quando já foi atingido.
  limite: { type: Number, default: null },
  // A versão publicada do agente transcreve nota de voz.
  ouveAudio: { type: Boolean, default: false },
  // No motor, a versão publicada não responde arquivo sem texto (L-19).
  anexoPedeTexto: { type: Boolean, default: false },
  // O agente analisa a foto e o PDF que recebe. Desligado, a conversa avisa antes do envio.
  leAnexos: { type: Boolean, default: true },
  // O agente lê o PDF: a leitura ligada e o par de modelos que lê documento, que o servidor manda
  // pronto. Sem isso, o PDF escolhido é avisado antes do envio.
  leDocumentos: { type: Boolean, default: true },
  // Para quem a conversa fala: `visitante` (a página do link) ou `equipe` (o Playground).
  publico: {
    type: String,
    default: 'visitante',
    validator: valor => ['visitante', 'equipe'].includes(valor),
  },
  // Quem testa vê a aba "Voz e anexos" (no Playground, o super admin): o motivo da nota de voz que
  // se conserta lá diz onde, como no Playground antes da conversa única. Quem não a vê lê só o
  // motivo.
  veVozEAnexos: { type: Boolean, default: false },
  // `pagina`: ocupa a altura que sobra na tela. `secao`: altura fixa, dentro de outra página.
  altura: {
    type: String,
    default: 'pagina',
    validator: valor => ['pagina', 'secao'].includes(valor),
  },
});

// O `recomecar` sai do `CabecalhoDaConversa`, e não daqui, embora a L-25 o liste entre os eventos
// da conversa: na página o cabeçalho fica à vista também na entrada e nas telas de estado, onde a
// conversa não existe. Quem usa a conversa monta os dois (o Playground, na L-26, também).
const emit = defineEmits(['enviar', 'reenviar', 'audio']);

const { t, locale } = useI18n();
const texto = ref('');
const listaRef = ref(null);

// A hora de cada bolha (L-18), HH:MM no fuso do navegador.
const hora = iso => horaDe(iso, locale.value);

// ── A foto e o PDF ────────────────────────────────────────────────
// { arquivo, nome, tipo, url }: o que vai na próxima mensagem.
const escolhidos = ref([]);
const avisoDosArquivos = ref('');
// O visor que não baixou (ver "O visor", abaixo).
const visorNaoAbriu = ref(false);
const seletorRef = ref(null);
const aceitos = TIPOS_ACEITOS.join(',');

// Arquivo sem texto, onde o agente publicado espera texto junto.
const faltaTexto = computed(
  () =>
    props.anexoPedeTexto && escolhidos.value.length > 0 && !texto.value.trim()
);
// Arquivo escolhido para um agente que não analisa arquivo: a conversa avisa antes do envio.
const naoAnalisa = computed(
  () => !props.leAnexos && escolhidos.value.length > 0
);
// Com a leitura ligada, o PDF ainda depende do modelo: o que não lê documento recebe o arquivo e diz
// que não consegue abrir.
const naoLePdf = computed(
  () =>
    props.leAnexos &&
    !props.leDocumentos &&
    escolhidos.value.some(escolhido => escolhido.tipo === 'pdf')
);
// O que está no campo pode ir: o texto, ou o arquivo que o agente aceita sem texto. Não depende
// da vez: com o agente respondendo, o botão segue sendo o enviar, desligado.
const temOQueEnviar = computed(
  () =>
    (Boolean(texto.value.trim()) || escolhidos.value.length > 0) &&
    !faltaTexto.value
);
const podeEnviar = computed(
  () => !props.ocupada && !props.limite && temOQueEnviar.value
);

function motivoDaRecusa(recusa, nome) {
  if (recusa === 'tipo') return t('CONVERSA_DE_TESTE.FILES.TYPE', { nome });
  if (recusa === 'grande')
    return t('CONVERSA_DE_TESTE.FILES.TOO_BIG', { nome });
  return t('CONVERSA_DE_TESTE.FILES.TOO_MANY', { n: POR_MENSAGEM });
}

// O que passa entra na lista; o primeiro que não passa diz por quê.
function escolher(evento) {
  const { escolhidos: lista, recusa } = escolherArquivos(
    escolhidos.value,
    Array.from(evento.target.files || [])
  );
  escolhidos.value = lista;
  avisoDosArquivos.value = recusa
    ? motivoDaRecusa(recusa.motivo, recusa.nome)
    : '';
  // O mesmo arquivo pode ser escolhido de novo depois de tirado.
  evento.target.value = '';
}

function tirar(indice) {
  liberarPrevias(escolhidos.value.splice(indice, 1));
  avisoDosArquivos.value = '';
  visorNaoAbriu.value = false;
}

// A bolha do turno faz a miniatura dela; a do campo sai com o envio, do texto ou da nota de voz.
function soltarEscolhidos() {
  const enviados = escolhidos.value;
  escolhidos.value = [];
  avisoDosArquivos.value = '';
  visorNaoAbriu.value = false;
  liberarPrevias(enviados);
}

function enviar(conteudo = texto.value) {
  const limpo = conteudo.trim();
  const arquivos = escolhidos.value.map(escolhido => escolhido.arquivo);
  if ((!limpo && !arquivos.length) || props.ocupada || props.limite) return;
  if (props.anexoPedeTexto && arquivos.length && !limpo) return;
  emit('enviar', limpo, arquivos);
  texto.value = '';
  soltarEscolhidos();
}

onBeforeUnmount(() => liberarPrevias(escolhidos.value));

// ── O visor ───────────────────────────────────────────────────────
// `{ indice }` abre os arquivos escolhidos, que ainda não foram; `{ indice, turno }`, os de uma
// bolha. O PDF que acabou de ir não tem endereço até o servidor devolver o dele, e abre pelo arquivo
// que o turno guarda para reenviar.
//
// Os anexos da bolha são lidos do turno a cada mudança, e não copiados ao abrir: quando o resultado
// traz os do servidor, o motor libera a miniatura `blob:` da bolha, e o visor passa para o endereço
// novo em vez de mostrar o que foi liberado. O turno que sai da conversa (recomeçar) fecha o visor.
//
// O visor baixa no primeiro clique. O que não baixa (a rede do celular caiu, ou um deploy trocou o
// endereço do pedaço com a página aberta) diz que não deu, e o próximo clique tenta de novo.
const visor = shallowRef(null);
const VisorDeAnexo = shallowRef(null);
async function abrirVisor(abertura) {
  try {
    VisorDeAnexo.value ||= (
      await import('shared/components/anexos/VisorDeAnexo.vue')
    ).default;
  } catch {
    visorNaoAbriu.value = true;
    return;
  }
  visorNaoAbriu.value = false;
  visor.value = abertura;
}
const anexosDaBolha = turno =>
  (turno.anexos || []).map((anexo, i) => ({
    ...anexo,
    arquivo: anexo.url ? null : turno.arquivos?.[i] || null,
  }));
const anexosDoVisor = computed(() => {
  const turno = visor.value?.turno;
  if (!turno) return escolhidos.value;
  return props.turnos.includes(turno) ? anexosDaBolha(turno) : [];
});
const abrirEscolhido = indice => abrirVisor({ indice });
const abrirDaBolha = (turno, indice) => abrirVisor({ indice, turno });
// O arquivo anotado entra no lugar do escolhido, com a miniatura dele; a do antigo sai da memória.
function trocarEscolhido({ indice, arquivo }) {
  const [antigo] = escolhidos.value.splice(indice, 1, {
    arquivo,
    ...paraATela(arquivo),
  });
  liberarPrevias([antigo]);
}

// O erro do turno chega como tipo (orçamento, prazo…); aqui ele vira frase.
const ERROS = {
  orcamento: 'CONVERSA_DE_TESTE.ERROR_BUDGET',
  prazo: 'CONVERSA_DE_TESTE.ERROR_TIMEOUT',
  catalogo: 'CONVERSA_DE_TESTE.ERROR_CATALOG',
  ferramenta: 'CONVERSA_DE_TESTE.ERROR_TOOL',
  rede: 'CONVERSA_DE_TESTE.ERROR_NETWORK',
};
// Para a equipe, como no Playground antes da conversa única: as ferramentas rodam de verdade no
// teste, e falham de verdade, e a frase aponta a configuração da ferramenta; e a consulta recusada
// pelo servidor (o pedido que o runtime não acha mais) diz o motivo que ele deu, no lugar da
// frase genérica.
const erroDoTurno = turno => {
  if (props.publico === 'equipe') {
    if (turno.erro === 'ferramenta')
      return t('CONVERSA_DE_TESTE.ERROR_TOOL_FOR_TEAM');
    if (turno.erro === 'perdido' && turno.detalhe) return turno.detalhe;
  }
  return t(ERROS[turno.erro] || 'CONVERSA_DE_TESTE.ERROR_GENERIC');
};
// O detalhe do erro é para a equipe, nunca para o visitante: o do turno, que o servidor manda cru ao
// super admin e limpo aos outros (sem modelo, valor nem exceção), e o da recusa do envio, que o
// rascunho diz em texto.
const detalheDoErro = turno =>
  (props.publico === 'equipe' && turno.detalhe) || undefined;

function falhaDoTurno(turno) {
  if (turno.falha === 'ocupado')
    return turno.espera
      ? t('CONVERSA_DE_TESTE.BUSY', { s: turno.espera })
      : t('CONVERSA_DE_TESTE.BUSY_SHORT');
  if (turno.falha === 'limite')
    return t('CONVERSA_DE_TESTE.LIMIT_REACHED', { n: props.limite });
  if (turno.falha === 'longa') return t('CONVERSA_DE_TESTE.TOO_LONG');
  if (turno.falha === 'anexo_tipo')
    return t('CONVERSA_DE_TESTE.FILES.TYPE', { nome: turno.falhaArquivo });
  if (turno.falha === 'anexo_grande')
    return t('CONVERSA_DE_TESTE.FILES.TOO_BIG', { nome: turno.falhaArquivo });
  if (turno.falha === 'anexos_demais')
    return t('CONVERSA_DE_TESTE.FILES.TOO_MANY', { n: POR_MENSAGEM });
  if (turno.falha === 'texto_obrigatorio')
    return t('CONVERSA_DE_TESTE.FILES.NEEDS_TEXT');
  if (turno.falha === 'canal_indisponivel')
    return t('CONVERSA_DE_TESTE.CHANNEL_UNAVAILABLE');
  if (turno.falha === 'recusada') return t('CONVERSA_DE_TESTE.SEND_REFUSED');
  return t('CONVERSA_DE_TESTE.SEND_FAILED');
}

const podeReenviar = turno =>
  ['ocupado', 'rede'].includes(turno.falha) && !turno.aguardando;

// O arquivo que o agente não leu. Com o switch "Ler imagens e documentos" desligado
// (`nao_analisado`) ele não falhou: não olha arquivo por configuração, e a frase não diz que ele
// "não conseguiu". Nos outros motivos, a equipe lê também por quê (ilegível, não baixou…), como
// no Playground antes da conversa única; o visitante, só que o agente não conseguiu.
const motivoDoArquivo = useMotivoDoArquivo();
const fraseDoNaoLido = ({ nome, motivo }) => {
  if (motivo === 'nao_analisado')
    return t('CONVERSA_DE_TESTE.FILES.ARRIVED_NOT_ANALYZED', { nome });
  if (props.publico === 'equipe' && motivo)
    return t('CONVERSA_DE_TESTE.FILES.NOT_READ_BECAUSE', {
      nome,
      motivo: motivoDoArquivo(motivo),
    });
  return t('CONVERSA_DE_TESTE.FILES.NOT_READ', { nome });
};

// ── A nota de voz ─────────────────────────────────────────────────
const {
  podeGravar,
  gravando,
  decorridoMs,
  niveis,
  erro: erroDoGravador,
  iniciar: iniciarGravacao,
  parar: pararGravacao,
  cancelar: cancelarGravacao,
} = useGravadorDeAudio();
const microfone = computed(() => props.ouveAudio && podeGravar);
// Um botão só para o microfone e o enviar (A-14): gravando, ele para e envia; com o que enviar, é o
// enviar; sem nada, é o microfone quando o agente ouve, e o enviar desligado quando não ouve. A foto
// sozinha para o agente que pede texto junto não pode ir: o botão é o microfone, e a voz completa a
// mensagem.
const botaoDoMicrofone = computed(
  () => microfone.value && !temOQueEnviar.value
);
const tempoGravado = computed(() => {
  const segundos = Math.floor(decorridoMs.value / 1000);
  return `${Math.floor(segundos / 60)}:${String(segundos % 60).padStart(2, '0')}`;
});
const avisoDoMicrofone = ref('');
// Do clique no microfone até a nota de voz sair. "Gravando" só acende no `start` do gravador (A-13),
// e antes dele o navegador pode estar pedindo a permissão do microfone: nesse meio tempo a conversa
// já espera a voz, e nada mais sai dela. A pergunta, o campo ou o reenviar deixariam o agente
// ocupado, e a gravação que começasse depois não iria mais.
const notaDeVozEmCurso = ref(false);
// A nota de voz que a tela espera. O cancelar passa a vez, e a que ficou para trás não mexe mais na
// tela quando o navegador responder.
let notaDaVez = 0;

// O mesmo botão grava e, gravando, envia. `iniciarGravacao` só resolve quando a gravação acaba:
// por este botão, pelo cancelar ou pelo teto de 2 minutos. A foto e o PDF escolhidos vão com a nota
// de voz, no mesmo turno (A-16): a transcrição é o texto que vai com eles.
async function alternarGravacao() {
  if (gravando.value) {
    pararGravacao();
    return;
  }
  if (notaDeVozEmCurso.value || props.ocupada || props.limite) return;
  avisoDoMicrofone.value = '';
  notaDeVozEmCurso.value = true;
  notaDaVez += 1;
  const esta = notaDaVez;
  const gravacao = await iniciarGravacao();
  if (esta !== notaDaVez) return;
  notaDeVozEmCurso.value = false;
  if (gravacao) {
    emit(
      'audio',
      gravacao,
      escolhidos.value.map(escolhido => escolhido.arquivo)
    );
    soltarEscolhidos();
    return;
  }
  if (erroDoGravador.value === 'microfone')
    avisoDoMicrofone.value = t('CONVERSA_DE_TESTE.AUDIO.MIC_DENIED');
  else if (erroDoGravador.value)
    avisoDoMicrofone.value = t('CONVERSA_DE_TESTE.AUDIO.RECORDER_FAILED');
}

// O cancelar vale também enquanto o navegador pede o microfone. O Firefox deixa o pedido pendente
// para sempre quando se fecha a pergunta clicando fora, e a conversa, que já esperava a voz, ficava
// travada até recarregar. O microfone que chegar depois o gravador descarta.
function cancelarNotaDeVoz() {
  notaDaVez += 1;
  notaDeVozEmCurso.value = false;
  cancelarGravacao();
}

// Por que o agente não ouviu. Motivo que a página não conhece cai no genérico em vez de sumir. A
// chave que falta e a transcrição desligada se consertam em "Voz e anexos": para quem vê a aba, a
// frase diz onde.
function motivoDoAudio(motivo) {
  const motivos = {
    sem_chave: props.veVozEAnexos
      ? t('CONVERSA_DE_TESTE.AUDIO.REASONS_FOR_TEAM.SEM_CHAVE')
      : t('CONVERSA_DE_TESTE.AUDIO.REASONS.SEM_CHAVE'),
    chave_invalida: t('CONVERSA_DE_TESTE.AUDIO.REASONS.CHAVE_INVALIDA'),
    sem_credito: t('CONVERSA_DE_TESTE.AUDIO.REASONS.SEM_CREDITO'),
    limite: t('CONVERSA_DE_TESTE.AUDIO.REASONS.LIMITE'),
    sem_fala: t('CONVERSA_DE_TESTE.AUDIO.REASONS.SEM_FALA'),
    prazo: t('CONVERSA_DE_TESTE.AUDIO.REASONS.PRAZO'),
    teto_de_testes: t('CONVERSA_DE_TESTE.AUDIO.REASONS.TETO_DE_TESTES'),
    desligada: props.veVozEAnexos
      ? t('CONVERSA_DE_TESTE.AUDIO.REASONS_FOR_TEAM.DESLIGADA')
      : t('CONVERSA_DE_TESTE.AUDIO.REASONS.DESLIGADA'),
    muitos: t('CONVERSA_DE_TESTE.AUDIO.REASONS.MUITOS'),
    rede: t('CONVERSA_DE_TESTE.AUDIO.REASONS.REDE'),
    invalido: t('CONVERSA_DE_TESTE.AUDIO.REASONS.INVALIDO'),
    servidor: t('CONVERSA_DE_TESTE.AUDIO.REASONS.SERVIDOR'),
    canal_indisponivel: t('CONVERSA_DE_TESTE.AUDIO.REASONS.CANAL_INDISPONIVEL'),
    erro: t('CONVERSA_DE_TESTE.AUDIO.REASONS.ERRO'),
  };
  return motivos[motivo] || motivos.erro;
}

// O agente está com a vez: o turno do motor que espera o resultado, ou a mensagem do canal que
// espera a primeira resposta.
const esperandoAgente = turno =>
  ['enviando', 'pending', 'aguardando'].includes(turno.status);

// Uma resposta nova, ou o "digitando", leva a lista para o fim.
watch(
  () =>
    props.turnos
      .map(
        turno =>
          `${turno.chave}:${turno.status}:${(turno.respostas || []).length}`
      )
      .join(),
  () =>
    nextTick(() => {
      const lista = listaRef.value;
      if (lista) lista.scrollTop = lista.scrollHeight;
    })
);
</script>

<template>
  <div
    class="flex flex-col"
    :class="altura === 'pagina' ? 'flex-1 min-h-0' : 'shrink-0'"
  >
    <div
      ref="listaRef"
      class="flex flex-col gap-3 px-4 py-5 overflow-y-auto"
      :class="
        altura === 'pagina'
          ? 'flex-1 min-h-0'
          : 'h-[clamp(12rem,calc(100dvh_-_30rem),26rem)]'
      "
    >
      <slot v-if="!turnos.length" name="vazio">
        <section
          v-if="oQueTestar"
          class="flex flex-col gap-1.5 px-4 py-3 border rounded-xl border-n-weak bg-n-alpha-1"
        >
          <p
            class="text-xs font-medium tracking-wide uppercase text-n-slate-10"
          >
            {{ t('CONVERSA_DE_TESTE.WHAT_TO_TEST') }}
          </p>
          <p class="text-sm whitespace-pre-line text-n-slate-12">
            {{ oQueTestar }}
          </p>
        </section>

        <!-- Com a nota de voz em curso (o microfone sendo pedido, ou gravando), a pergunta sugerida
             espera, como o reenviar: ela levaria a foto que vai com a nota de voz, e a nota, com o
             agente ocupado, não iria mais. -->
        <section v-if="perguntas.length" class="flex flex-col gap-2">
          <p
            class="text-xs font-medium tracking-wide uppercase text-n-slate-10"
          >
            {{ t('CONVERSA_DE_TESTE.SUGGESTED') }}
          </p>
          <div class="flex flex-wrap gap-2">
            <button
              v-for="(pergunta, i) in perguntas"
              :key="i"
              type="button"
              class="px-3 py-2 text-sm text-left transition-colors border rounded-xl border-n-weak bg-n-solid-2 text-n-slate-12 hover:enabled:bg-n-alpha-2 disabled:opacity-50 disabled:cursor-not-allowed"
              :disabled="ocupada || Boolean(limite) || notaDeVozEmCurso"
              @click="enviar(pergunta)"
            >
              {{ pergunta }}
            </button>
          </div>
        </section>

        <p class="m-auto text-sm text-center text-n-slate-10">
          {{ t('CONVERSA_DE_TESTE.EMPTY') }}
        </p>
      </slot>

      <template v-for="turno in turnos" :key="turno.chave">
        <div
          v-if="turno.audio || turno.pergunta || turno.anexos?.length"
          class="flex flex-col items-end gap-1"
        >
          <div
            v-if="turno.audio"
            class="flex flex-col gap-1.5 max-w-[85%] px-3.5 py-2 text-sm break-words shadow-sm rounded-2xl rounded-br-md"
            :class="
              turno.audio.estado === 'falhou'
                ? 'bg-n-ruby-3 text-n-ruby-11'
                : 'bg-n-brand text-white'
            "
            :data-audio="turno.audio.estado"
          >
            <AnexosDaBolha
              v-if="turno.anexos?.length"
              :anexos="anexosDaBolha(turno)"
              @abrir="indice => abrirDaBolha(turno, indice)"
            />
            <AudioEnviado
              v-if="turno.som?.url"
              :url="turno.som.url"
              :duracao-ms="turno.som.duracaoMs || null"
            />
            <div class="flex items-start gap-1.5">
              <i
                class="mt-0.5 shrink-0"
                :class="
                  turno.audio.estado === 'falhou'
                    ? 'i-lucide-mic-off'
                    : 'i-lucide-mic'
                "
              />
              <span v-if="turno.audio.estado === 'pronto'">
                {{
                  t('CONVERSA_DE_TESTE.AUDIO.HEARD', {
                    texto: turno.audio.texto,
                  })
                }}
              </span>
              <span v-else-if="turno.audio.estado === 'falhou'">
                {{
                  t('CONVERSA_DE_TESTE.AUDIO.NOT_HEARD', {
                    motivo: motivoDoAudio(turno.audio.motivo),
                  })
                }}
              </span>
              <span v-else class="animate-pulse">
                {{ t('CONVERSA_DE_TESTE.AUDIO.TRANSCRIBING') }}
              </span>
              <time
                v-if="turno.criada_em"
                :datetime="turno.criada_em"
                class="self-end ml-1 text-[11px] opacity-80 shrink-0"
                data-hora
              >
                {{ hora(turno.criada_em) }}
              </time>
            </div>
          </div>
          <div
            v-else
            class="max-w-[85%] px-3.5 py-2 text-sm break-words shadow-sm bg-n-brand text-white rounded-2xl rounded-br-md"
            :class="{ 'opacity-60': turno.status === 'falhou' }"
          >
            <AnexosDaBolha
              v-if="turno.anexos?.length"
              class="mb-1"
              :anexos="anexosDaBolha(turno)"
              @abrir="indice => abrirDaBolha(turno, indice)"
            />
            <span v-if="turno.pergunta" class="whitespace-pre-wrap">
              {{ turno.pergunta }}
            </span>
            <time
              v-if="turno.criada_em"
              :datetime="turno.criada_em"
              class="block mt-1 text-[11px] text-right opacity-80"
              data-hora
            >
              {{ hora(turno.criada_em) }}
            </time>
          </div>
          <div
            v-if="turno.status === 'falhou'"
            class="flex flex-wrap items-center justify-end gap-2 text-xs text-n-ruby-11"
            role="alert"
          >
            <span>{{ falhaDoTurno(turno) }}</span>
            <Button
              v-if="podeReenviar(turno)"
              type="button"
              :label="t('CONVERSA_DE_TESTE.RESEND')"
              icon="i-lucide-refresh-cw"
              ruby
              link
              xs
              :disabled="ocupada || notaDeVozEmCurso"
              @click="emit('reenviar', turno)"
            />
            <!-- O motivo da recusa em texto (a ferramenta malformada do rascunho, por exemplo): é o
                 que diz à equipe o que consertar. -->
            <span
              v-if="detalheDoErro(turno)"
              class="text-right break-words basis-full"
              data-detalhe-da-falha
            >
              {{ detalheDoErro(turno) }}
            </span>
          </div>
        </div>

        <div
          v-for="resposta in turno.respostas || []"
          :key="resposta.chave"
          class="flex flex-col items-start gap-1.5"
          :data-resposta="resposta.id"
        >
          <span
            v-if="resposta.de === 'equipe'"
            class="px-1.5 py-0.5 text-[11px] font-medium rounded-md bg-n-alpha-2 text-n-slate-11"
          >
            {{ t('CONVERSA_DE_TESTE.TEAM_REPLY') }}
          </span>
          <RespostaDoAgente
            v-if="resposta.texto"
            :texto="resposta.texto"
            :quando="resposta.criada_em || ''"
          />
          <img
            v-for="(imagem, i) in resposta.imagens || []"
            :key="`${resposta.chave}-img-${i}`"
            :src="imagem.url"
            :alt="t('CONVERSA_DE_TESTE.AGENT_IMAGE')"
            loading="lazy"
            class="max-w-[85%] max-h-72 rounded-2xl rounded-bl-md border border-n-weak"
          />
        </div>

        <div
          v-if="esperandoAgente(turno)"
          class="flex justify-start"
          role="status"
          :aria-label="t('CONVERSA_DE_TESTE.TYPING')"
        >
          <div
            class="flex items-center gap-1 px-3.5 py-2.5 rounded-2xl rounded-bl-md bg-n-solid-iris"
          >
            <span class="rounded-full size-1.5 bg-n-slate-9 animate-bounce" />
            <span
              class="rounded-full size-1.5 bg-n-slate-9 animate-bounce [animation-delay:0.15s]"
            />
            <span
              class="rounded-full size-1.5 bg-n-slate-9 animate-bounce [animation-delay:0.3s]"
            />
          </div>
        </div>

        <div
          v-else-if="turno.status === 'done'"
          class="flex flex-col items-start gap-1.5"
        >
          <RespostaDoAgente
            v-for="(parte, i) in turno.partes"
            :key="`${turno.chave}-${i}`"
            :texto="parte"
            :quando="turno.respondida_em || ''"
          />
          <img
            v-for="(imagem, i) in turno.imagens || []"
            :key="`${turno.chave}-img-${i}`"
            :src="imagem.url"
            :alt="imagem.caption || t('CONVERSA_DE_TESTE.AGENT_IMAGE')"
            loading="lazy"
            class="max-w-[85%] max-h-72 rounded-2xl rounded-bl-md border border-n-weak"
          />
          <p
            v-if="turno.transferencia"
            class="px-2 py-1 text-xs rounded-md bg-n-amber-3 text-n-amber-11"
          >
            {{ t('CONVERSA_DE_TESTE.HANDOFF') }}
          </p>
          <p
            v-if="turno.funil"
            class="px-2 py-1 text-xs rounded-md bg-n-alpha-2 text-n-slate-11"
          >
            {{ t('CONVERSA_DE_TESTE.FUNNEL') }}
          </p>
          <p
            v-for="(naoLido, i) in turno.nao_lidos || []"
            :key="`${turno.chave}-nao-lido-${i}`"
            class="px-2 py-1 text-xs rounded-md bg-n-amber-3 text-n-amber-11"
            data-nao-lido
          >
            {{ fraseDoNaoLido(naoLido) }}
          </p>
        </div>

        <p
          v-else-if="turno.status === 'error'"
          class="self-center max-w-[85%] px-3 py-1.5 text-xs text-center rounded-lg bg-n-ruby-3 text-n-ruby-11"
          role="alert"
          :title="detalheDoErro(turno)"
        >
          {{ erroDoTurno(turno) }}
        </p>

        <p
          v-else-if="turno.status === 'sem_resposta'"
          class="self-center max-w-[85%] px-3 py-1.5 text-xs text-center rounded-lg bg-n-alpha-2 text-n-slate-11"
          role="status"
        >
          {{ t('CONVERSA_DE_TESTE.NO_REPLY_YET') }}
        </p>

        <slot name="depois-da-resposta" :turno="turno" />
      </template>
    </div>

    <p
      v-if="limite"
      class="px-4 py-2 text-xs text-center border-t shrink-0 border-n-weak bg-n-amber-3 text-n-amber-11"
      role="status"
    >
      {{ t('CONVERSA_DE_TESTE.LIMIT_REACHED', { n: limite }) }}
    </p>
    <p
      v-if="avisoDoMicrofone"
      class="px-4 py-2 text-xs text-center border-t shrink-0 border-n-weak bg-n-ruby-3 text-n-ruby-11"
      role="alert"
    >
      {{ avisoDoMicrofone }}
    </p>
    <!-- O que vai com a próxima mensagem: a miniatura da foto ou o nome do PDF, e o motivo do que
         não entrou. -->
    <div
      v-if="
        escolhidos.length ||
        avisoDosArquivos ||
        visorNaoAbriu ||
        faltaTexto ||
        naoAnalisa ||
        naoLePdf
      "
      class="flex flex-col gap-2 px-3 pt-2 border-t shrink-0 border-n-weak bg-n-solid-1"
    >
      <!-- `pt-1` deixa lugar para o X, que sai um pouco acima do canto da miniatura. -->
      <div v-if="escolhidos.length" class="flex flex-wrap gap-2.5 pt-1">
        <ChipDeAnexo
          v-for="(escolhido, i) in escolhidos"
          :key="escolhido.url || `${escolhido.nome}-${i}`"
          :anexo="escolhido"
          @abrir="abrirEscolhido(i)"
          @tirar="tirar(i)"
        />
      </div>
      <p
        v-if="avisoDosArquivos"
        class="text-xs text-n-ruby-11"
        role="alert"
        data-aviso-arquivo
      >
        {{ avisoDosArquivos }}
      </p>
      <p
        v-if="visorNaoAbriu"
        class="text-xs text-n-ruby-11"
        role="alert"
        data-visor-nao-abriu
      >
        {{ t('CONVERSA_DE_TESTE.VIEWER.FAILED') }}
      </p>
      <p
        v-if="faltaTexto"
        class="text-xs text-n-amber-11"
        role="status"
        data-falta-texto
      >
        {{ t('CONVERSA_DE_TESTE.FILES.NEEDS_TEXT') }}
      </p>
      <p
        v-if="naoAnalisa"
        class="text-xs text-n-amber-11"
        role="status"
        data-nao-analisa
      >
        <slot name="nao-analisa">
          {{ t('CONVERSA_DE_TESTE.FILES.NOT_ANALYZED') }}
        </slot>
      </p>
      <p
        v-if="naoLePdf"
        class="text-xs text-n-amber-11"
        role="status"
        data-nao-le-pdf
      >
        {{ t('CONVERSA_DE_TESTE.FILES.PDF_NOT_READ') }}
      </p>
    </div>
    <form
      class="flex items-end gap-2 px-3 pt-3 pb-2 border-t shrink-0 border-n-weak bg-n-solid-1"
      @submit.prevent="enviar()"
    >
      <!-- Gravando: o tempo, as ondas e o cancelar no lugar do campo; o botão ao lado envia.
           No celular (360 px) o "Cancelar" encolhia como qualquer item flexível e o rótulo saía
           "Canc…". Ele não encolhe mais, e o enviar abre o espaço ficando só com o ícone abaixo
           de `sm`: o rótulo segue para o leitor de tela (sr-only). É o ajuste do Playground.
           Antes do "gravando", enquanto o navegador pede o microfone, a barra já está aqui, sem
           tempo nem ondas, e com o mesmo cancelar. -->
      <div
        v-if="gravando || notaDeVozEmCurso"
        class="flex items-center flex-1 min-w-0 gap-2 px-1 py-1.5 text-sm text-n-slate-12"
        role="status"
      >
        <template v-if="gravando">
          <span
            class="rounded-full size-2 shrink-0 bg-n-ruby-9 animate-pulse"
          />
          <span class="tabular-nums shrink-0">
            {{
              t('CONVERSA_DE_TESTE.AUDIO.RECORDING', { tempo: tempoGravado })
            }}
          </span>
          <OndasDoMicrofone
            class="flex-1 min-w-0 text-n-slate-11"
            :niveis="niveis"
          />
        </template>
        <span v-else class="flex-1 min-w-0 text-n-slate-11" data-pedindo>
          {{ t('CONVERSA_DE_TESTE.AUDIO.WAITING_MIC') }}
        </span>
        <Button
          type="button"
          :label="t('CONVERSA_DE_TESTE.AUDIO.CANCEL')"
          slate
          faded
          xs
          class="ml-auto shrink-0"
          @click="cancelarNotaDeVoz"
        />
      </div>
      <Input
        v-else
        v-model="texto"
        class="flex-1"
        :placeholder="t('CONVERSA_DE_TESTE.PLACEHOLDER')"
        :disabled="Boolean(limite)"
      />
      <!-- `relative` segura o rótulo sr-only dentro do botão: sem ancestral posicionado, o span
           absoluto escapa da rolagem e faz a página inteira rolar. -->
      <Button
        v-if="gravando"
        type="button"
        icon="i-lucide-send"
        class="relative shrink-0"
        @click="alternarGravacao"
      >
        <span class="min-w-0 sr-only sm:not-sr-only sm:truncate">
          {{ t('CONVERSA_DE_TESTE.AUDIO.SEND') }}
        </span>
      </Button>
      <template v-else>
        <input
          ref="seletorRef"
          type="file"
          class="hidden"
          multiple
          :accept="aceitos"
          data-seletor
          @change="escolher"
        />
        <Button
          type="button"
          icon="i-lucide-paperclip"
          slate
          faded
          :disabled="ocupada || Boolean(limite) || notaDeVozEmCurso"
          :title="t('CONVERSA_DE_TESTE.FILES.ATTACH')"
          :aria-label="t('CONVERSA_DE_TESTE.FILES.ATTACH')"
          @click="seletorRef?.click()"
        />
        <Button
          v-if="botaoDoMicrofone"
          type="button"
          icon="i-lucide-mic"
          slate
          faded
          :disabled="ocupada || Boolean(limite) || notaDeVozEmCurso"
          :title="t('CONVERSA_DE_TESTE.AUDIO.RECORD')"
          :aria-label="t('CONVERSA_DE_TESTE.AUDIO.RECORD')"
          @click="alternarGravacao"
        />
        <Button
          v-else
          type="submit"
          :label="t('CONVERSA_DE_TESTE.SEND')"
          icon="i-lucide-send"
          :disabled="!podeEnviar"
        />
      </template>
    </form>

    <component
      :is="VisorDeAnexo"
      v-if="visor"
      :anexos="anexosDoVisor"
      :inicial="visor.indice"
      :pode-anotar="!visor.turno"
      @fechar="visor = null"
      @anotado="trocarEscolhido"
    />
  </div>
</template>
